import { MakeLogicType, actions, kea, key, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { actionToUrl, router, urlToAction } from 'kea-router'

import { LemonDialog, PaginationManual, lemonToast } from '@posthog/lemon-ui'

import api, { CountedPaginatedResponse } from 'lib/api'
import { objectsEqual } from 'lib/utils/objects'
import { urls } from 'scenes/urls'

import { deleteFromTree } from '~/layout/panel-layout/ProjectTree/projectTreeLogic'

import type { HogFlow } from './hogflows/types'
import { prepareWorkflowDuplicate } from './workflowDuplication'

export type WorkflowStatusFilter = 'all' | 'active' | 'draft' | 'archived'

const WORKFLOW_STATUS_FILTERS: WorkflowStatusFilter[] = ['all', 'active', 'draft', 'archived']

export type WorkflowTypeFilter = 'all' | 'messaging' | 'automation'

const WORKFLOW_TYPE_FILTERS: WorkflowTypeFilter[] = ['all', 'messaging', 'automation']

export type WorkflowTriggerTypeFilter = 'all' | (NonNullable<HogFlow['trigger']> extends { type: infer T } ? T : never)

// Keep in sync with TRIGGER_TYPES in products/workflows/backend/models/hog_flow/hog_flow.py.
export const WORKFLOW_TRIGGER_TYPE_OPTIONS: { value: WorkflowTriggerTypeFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'event', label: 'Event' },
    { value: 'schedule', label: 'Schedule' },
    { value: 'manual', label: 'Manual' },
    { value: 'batch', label: 'Batch' },
    { value: 'webhook', label: 'Webhook' },
    { value: 'tracking_pixel', label: 'Tracking pixel' },
    { value: 'data-warehouse-table', label: 'Data warehouse table' },
    { value: 'data-warehouse-view', label: 'Data warehouse view' },
    { value: 'internal-event', label: 'Internal event' },
]

const WORKFLOW_TRIGGER_TYPE_FILTERS = WORKFLOW_TRIGGER_TYPE_OPTIONS.map((option) => option.value)

export const WORKFLOWS_PER_PAGE = 30

export interface WorkflowsFilters {
    search: string
    createdBy: string | null
    status: WorkflowStatusFilter
    type: WorkflowTypeFilter
    triggerType: WorkflowTriggerTypeFilter
    page: number
}

const DEFAULT_FILTERS: WorkflowsFilters = {
    search: '',
    createdBy: null,
    status: 'all',
    type: 'all',
    triggerType: 'all',
    page: 1,
}

type WorkflowsResult = CountedPaginatedResponse<HogFlow>

interface WorkflowsListParams {
    search?: string
    status?: HogFlow['status']
    created_by?: string
    type?: Exclude<WorkflowTypeFilter, 'all'>
    trigger?: string
    limit: number
    offset: number
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowsLogicValues {
    allArchivedSelected: boolean
    filters: WorkflowsFilters
    hasLoadedWorkflows: boolean
    pagination: PaginationManual
    paramsFromFilters: WorkflowsListParams
    selectedArchivedCount: number
    selectedArchivedWorkflowIds: Set<string>
    workflows: WorkflowsResult
    workflowsLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowsLogicActions {
    archiveWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    archiveWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    archiveWorkflowSuccess: (
        workflows: WorkflowsResult,
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: WorkflowsResult
        payload?: {
            workflow: HogFlow
        }
    }
    clearArchivedWorkflowSelection: () => {
        value: true
    }
    deleteSelectedWorkflows: () => {
        value: true
    }
    deleteWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    deleteWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteWorkflowSuccess: (
        workflows: WorkflowsResult,
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: WorkflowsResult
        payload?: {
            workflow: HogFlow
        }
    }
    duplicateWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    duplicateWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    duplicateWorkflowSuccess: (
        workflows: WorkflowsResult,
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: WorkflowsResult
        payload?: {
            workflow: HogFlow
        }
    }
    loadWorkflows: () => {}
    loadWorkflowsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadWorkflowsSuccess: (
        workflows: CountedPaginatedResponse<HogFlow>,
        payload?: {}
    ) => {
        workflows: CountedPaginatedResponse<HogFlow>
        payload?: {}
    }
    restoreWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    restoreWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    restoreWorkflowSuccess: (
        workflows: WorkflowsResult,
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: WorkflowsResult
        payload?: {
            workflow: HogFlow
        }
    }
    selectAllArchivedWorkflows: (ids: string[]) => {
        ids: string[]
    }
    setFilters: (
        filters: Partial<WorkflowsFilters>,
        replace?: boolean
    ) => {
        filters: Partial<WorkflowsFilters>
        replace: boolean | undefined
    }
    toggleArchivedWorkflowSelection: (id: string) => {
        id: string
    }
    toggleWorkflowStatus: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    toggleWorkflowStatusFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    toggleWorkflowStatusSuccess: (
        workflows: WorkflowsResult,
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: WorkflowsResult
        payload?: {
            workflow: HogFlow
        }
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowsLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        paramsFromFilters: (filters: WorkflowsFilters) => WorkflowsListParams
        pagination: (filters: WorkflowsFilters, workflows: WorkflowsResult) => PaginationManual
        allArchivedSelected: (workflows: WorkflowsResult, selectedArchivedWorkflowIds: Set<string>) => boolean
        selectedArchivedCount: (selectedArchivedWorkflowIds: Set<string>) => number
    }
}

export type workflowsLogicType = MakeLogicType<
    workflowsLogicValues,
    workflowsLogicActions,
    Record<string, any>,
    workflowsLogicMeta
>

export const workflowsLogic = kea<workflowsLogicType>([
    key(() => 'workflowsLogic'),
    path(['products', 'workflows', 'frontend', 'workflowsLogic']),
    actions({
        toggleWorkflowStatus: (workflow: HogFlow) => ({ workflow }),
        duplicateWorkflow: (workflow: HogFlow) => ({ workflow }),
        archiveWorkflow: (workflow: HogFlow) => ({ workflow }),
        restoreWorkflow: (workflow: HogFlow) => ({ workflow }),
        deleteWorkflow: (workflow: HogFlow) => ({ workflow }),
        deleteSelectedWorkflows: true,
        loadWorkflows: () => ({}),
        setFilters: (filters: Partial<WorkflowsFilters>, replace?: boolean) => ({ filters, replace }),
        toggleArchivedWorkflowSelection: (id: string) => ({ id }),
        selectAllArchivedWorkflows: (ids: string[]) => ({ ids }),
        clearArchivedWorkflowSelection: true,
    }),
    reducers({
        filters: [
            DEFAULT_FILTERS,
            {
                setFilters: (state, { filters, replace }) => {
                    const next = replace ? { ...DEFAULT_FILTERS, ...filters } : { ...state, ...filters }
                    // Any filter change other than paging jumps back to the first page, so results stay
                    // in sync with what the user just typed/picked.
                    if (!('page' in filters)) {
                        next.page = 1
                    }
                    return next
                },
            },
        ],
        selectedArchivedWorkflowIds: [
            new Set<string>(),
            {
                toggleArchivedWorkflowSelection: (state, { id }) => {
                    const next = new Set(state)
                    if (next.has(id)) {
                        next.delete(id)
                    } else {
                        next.add(id)
                    }
                    return next
                },
                selectAllArchivedWorkflows: (_, { ids }) => new Set<string>(ids),
                clearArchivedWorkflowSelection: () => new Set<string>(),
                loadWorkflowsSuccess: () => new Set<string>(),
            },
        ],
        hasLoadedWorkflows: [
            false as boolean,
            {
                loadWorkflowsSuccess: () => true,
            },
        ],
    }),
    loaders(({ actions, values }) => ({
        workflows: [
            { results: [], count: 0 } as WorkflowsResult,
            {
                loadWorkflows: async (_, breakpoint) => {
                    const response = await api.hogFlows.getHogFlows(values.paramsFromFilters)
                    // Drop a response a newer filter change has already superseded, so a slow request
                    // returning after a faster later one can't leave the table showing the wrong filters.
                    breakpoint()
                    return response
                },
                toggleWorkflowStatus: async ({ workflow }) => {
                    await api.hogFlows.updateHogFlow(workflow.id, {
                        status: workflow.status === 'active' ? 'draft' : 'active',
                    })
                    // Reload instead of patching in place: the new status may no longer match the
                    // active status filter, so the row has to be re-fetched through the server query.
                    actions.loadWorkflows()
                    return values.workflows
                },
                duplicateWorkflow: async ({ workflow }) => {
                    await api.hogFlows.createHogFlow(prepareWorkflowDuplicate(workflow))
                    // The copy is a draft; reload so it only shows when it matches the current filter
                    // and lands in the right spot under the server-side sort and pagination.
                    actions.loadWorkflows()
                    return values.workflows
                },
                archiveWorkflow: async ({ workflow }) => {
                    LemonDialog.open({
                        width: 500,
                        title: 'Archive workflow?',
                        description: `Are you sure you want to archive "${workflow.name}"?${
                            workflow.status === 'active'
                                ? ' In-progress workflow invocations will end without completing.'
                                : ''
                        }`,
                        primaryButton: {
                            children: 'Archive',
                            type: 'primary',
                            status: 'danger',
                            onClick: async () => {
                                try {
                                    await api.hogFlows.updateHogFlow(workflow.id, {
                                        status: 'archived',
                                    })
                                    lemonToast.success(`Workflow "${workflow.name}" archived`)
                                    router.actions.push(urls.workflows())
                                    actions.loadWorkflows()
                                } catch (error: any) {
                                    lemonToast.error(
                                        `Failed to archive workflow: ${error.detail || error.message || 'Unknown error'}`
                                    )
                                }
                            },
                        },
                        secondaryButton: {
                            children: 'Cancel',
                        },
                    })
                    // Return unchanged workflows since dialog handles the update
                    return values.workflows
                },
                restoreWorkflow: async ({ workflow }) => {
                    try {
                        await api.hogFlows.updateHogFlow(workflow.id, {
                            status: 'draft',
                        })
                        lemonToast.success(`Workflow "${workflow.name}" restored to draft status`)
                        // Restored workflows become drafts, so they drop out of the archived filter —
                        // reload rather than keep the stale row in the current filtered page.
                        actions.loadWorkflows()
                        return values.workflows
                    } catch (error: any) {
                        lemonToast.error(
                            `Failed to restore workflow: ${error?.detail || error?.message || 'Unknown error'}`
                        )
                        return values.workflows
                    }
                },
                deleteWorkflow: async ({ workflow }) => {
                    LemonDialog.open({
                        width: 500,
                        title: 'Delete workflow?',
                        description: `Are you sure you want to permanently delete "${workflow.name}"? This action cannot be undone.`,
                        primaryButton: {
                            children: 'Delete',
                            type: 'primary',
                            status: 'danger',
                            onClick: async () => {
                                try {
                                    await api.hogFlows.deleteHogFlow(workflow.id)
                                    lemonToast.success(`Workflow "${workflow.name}" deleted`)
                                    deleteFromTree('hog_flow/', workflow.id)
                                    actions.loadWorkflows()
                                } catch (error: any) {
                                    lemonToast.error(
                                        `Failed to delete workflow: ${error.detail || error.message || 'Unknown error'}`
                                    )
                                }
                            },
                        },
                        secondaryButton: {
                            children: 'Cancel',
                        },
                    })
                    return values.workflows
                },
            },
        ],
    })),
    selectors({
        paramsFromFilters: [
            (s) => [s.filters],
            (filters: WorkflowsFilters): WorkflowsListParams => ({
                search: filters.search || undefined,
                status: filters.status !== 'all' ? filters.status : undefined,
                created_by: filters.createdBy || undefined,
                type: filters.type !== 'all' ? filters.type : undefined,
                // The API filters triggers by JSON containment, so the type goes over as a JSON object.
                trigger: filters.triggerType !== 'all' ? JSON.stringify({ type: filters.triggerType }) : undefined,
                limit: WORKFLOWS_PER_PAGE,
                offset: filters.page ? (filters.page - 1) * WORKFLOWS_PER_PAGE : 0,
            }),
        ],
        pagination: [
            (s) => [s.filters, s.workflows],
            (filters: WorkflowsFilters, workflows: WorkflowsResult): PaginationManual => ({
                controlled: true,
                pageSize: WORKFLOWS_PER_PAGE,
                currentPage: filters.page || 1,
                entryCount: workflows.count,
            }),
        ],
        allArchivedSelected: [
            (s) => [s.workflows, s.selectedArchivedWorkflowIds],
            (workflows: WorkflowsResult, selectedIds: Set<string>): boolean => {
                return workflows.results.length > 0 && workflows.results.every((w) => selectedIds.has(w.id))
            },
        ],
        selectedArchivedCount: [
            (s) => [s.selectedArchivedWorkflowIds],
            (selectedIds: Set<string>): number => selectedIds.size,
        ],
    }),
    listeners(({ actions, values }) => ({
        setFilters: async (_, breakpoint) => {
            // Debounce so typing in the search box doesn't fire a request per keystroke.
            await breakpoint(300)
            actions.loadWorkflows()
        },
        loadWorkflowsSuccess: ({ workflows }) => {
            // A page past the end (stale link, or rows deleted since) would otherwise leave the user on a
            // blank table with no pagination control to click back with, so land them on the last real page.
            const lastPage = Math.max(1, Math.ceil(workflows.count / WORKFLOWS_PER_PAGE))
            if (values.filters.page > lastPage) {
                actions.setFilters({ page: lastPage })
            }
        },
        loadWorkflowsFailure: ({ error }) => {
            lemonToast.error(`Failed to load workflows: ${error || 'Unknown error'}`)
        },
        deleteSelectedWorkflows: () => {
            const ids = Array.from(values.selectedArchivedWorkflowIds)
            if (ids.length === 0) {
                return
            }
            LemonDialog.open({
                width: 500,
                title: `Delete ${ids.length} archived workflow${ids.length === 1 ? '' : 's'}?`,
                description: `Are you sure you want to permanently delete ${ids.length} archived workflow${ids.length === 1 ? '' : 's'}? This action cannot be undone.`,
                primaryButton: {
                    children: 'Delete',
                    type: 'primary',
                    status: 'danger',
                    onClick: async () => {
                        try {
                            const result = await api.hogFlows.bulkDeleteHogFlows(ids)
                            lemonToast.success(`${result.deleted} workflow${result.deleted === 1 ? '' : 's'} deleted`)
                            for (const id of ids) {
                                deleteFromTree('hog_flow/', id)
                            }
                            actions.clearArchivedWorkflowSelection()
                            actions.loadWorkflows()
                        } catch (error: any) {
                            lemonToast.error(
                                `Failed to delete workflows: ${error?.detail || error?.message || 'Unknown error'}`
                            )
                        }
                    },
                },
                secondaryButton: {
                    children: 'Cancel',
                },
            })
        },
    })),
    actionToUrl(({ values }) => {
        const buildURL = (): [string, Record<string, any>, Record<string, any>, { replace: boolean }] => {
            const searchParams = { ...router.values.searchParams }

            const setOrDelete = (key: string, value: string | number | null | undefined): void => {
                if (value) {
                    searchParams[key] = value
                } else {
                    delete searchParams[key]
                }
            }

            setOrDelete('search', values.filters.search)
            setOrDelete('created_by', values.filters.createdBy)
            setOrDelete('status', values.filters.status !== 'all' ? values.filters.status : null)
            setOrDelete('type', values.filters.type !== 'all' ? values.filters.type : null)
            setOrDelete('trigger_type', values.filters.triggerType !== 'all' ? values.filters.triggerType : null)
            setOrDelete('page', values.filters.page > 1 ? values.filters.page : null)

            return [router.values.location.pathname, searchParams, router.values.hashParams, { replace: true }]
        }

        return {
            setFilters: buildURL,
        }
    }),
    urlToAction(({ actions, values }) => ({
        [urls.workflows()]: (_, searchParams) => {
            const status = searchParams['status']
            const type = searchParams['type']
            const triggerType = searchParams['trigger_type']
            const parsed: WorkflowsFilters = {
                search: searchParams['search'] ? String(searchParams['search']) : '',
                createdBy: searchParams['created_by'] ? String(searchParams['created_by']) : null,
                status: WORKFLOW_STATUS_FILTERS.includes(status) ? status : 'all',
                type: WORKFLOW_TYPE_FILTERS.includes(type) ? type : 'all',
                triggerType: WORKFLOW_TRIGGER_TYPE_FILTERS.includes(triggerType) ? triggerType : 'all',
                // Anything unparseable, zero, or negative falls back to page 1 rather than reaching
                // the offset maths as NaN or a negative number.
                page: Math.max(1, parseInt(String(searchParams['page'])) || 1),
            }
            if (!objectsEqual(parsed, values.filters)) {
                actions.setFilters(parsed, true)
            }
        },
    })),
])
